import { GROUP_SUNO } from "constants/staff";
import { decodeJwtPayload } from "utils/auth-header.util";

// Kept free of the `import.meta` import chain (no auth-slice import) so ts-jest
// can unit-test the persona logic — see project CLAUDE.md gotcha.

const toNumber = (value: unknown): number | null => {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

export interface ActiveSessionContext {
  // The session authenticates with a Bearer JWT (only Bearer sessions can be
  // switched / break-glass).
  isBearer: boolean;
  // The active practice differs from the user's home practice.
  isSwitched: boolean;
  // A short-lived internal break-glass session.
  isBreakGlass: boolean;
  // The acting user is internal Suno staff.
  isSunoActor: boolean;
  // Internal/admin persona (Suno staff or break-glass) — drives the RED header
  // regardless of whether they've switched yet. Franchise (grant) users are
  // not internal and get the neutral "Viewing" header.
  isInternal: boolean;
  // Show the red "Admin Mode" warning (internal AND switched). Kept for the
  // switched-state labelling; `isInternal` drives the persistent header colour.
  isAdminMode: boolean;
  homePracticeId: number | null;
  activePracticeId: number | null;
  activePracticeName: string | null;
}

export interface SessionContextInput {
  token: string;
  scheme: string;
  groupNames: string[];
  activePracticeId?: number | null;
  activePracticeName?: string | null;
}

/**
 * Pure persona derivation. Reads the Bearer token's claims (`practice_id`,
 * `home_practice_id`, `break_glass`) and combines them with the actor's groups +
 * the active practice to decide which context banner (if any) to show.
 */
export function computeActiveSessionContext({
  token,
  scheme,
  groupNames,
  activePracticeId: activePracticeIdFromState = null,
  activePracticeName = null
}: SessionContextInput): ActiveSessionContext {
  const isBearer = scheme === "Bearer";
  const claims = isBearer && token ? decodeJwtPayload(token) : null;

  const homePracticeId = toNumber(claims?.home_practice_id);
  const activePracticeId =
    toNumber(claims?.practice_id) ?? activePracticeIdFromState ?? null;
  const isBreakGlass = claims?.break_glass === true;
  const isSunoActor = groupNames.includes(GROUP_SUNO);
  const isInternal = isSunoActor || isBreakGlass;
  const isSwitched =
    isBearer &&
    homePracticeId != null &&
    activePracticeId != null &&
    homePracticeId !== activePracticeId;
  // Red "Admin Mode" only once an internal user has actually switched into a
  // foreign practice; the persistent header colour itself uses `isInternal`.
  const isAdminMode = isSwitched && isInternal;

  return {
    isBearer,
    isSwitched,
    isBreakGlass,
    isSunoActor,
    isInternal,
    isAdminMode,
    homePracticeId,
    activePracticeId,
    activePracticeName
  };
}
